import { apiFetch } from "./apiFetch";

/** Lifecycle of a partner purchase. `paid` is the stuck state this screen is for. */
export type PartnerClaimStatus =
  | "created"
  | "paid"
  | "claimed"
  | "attached_by_support";

/**
 * One paid-but-unclaimed partner purchase.
 *
 * All timestamps are epoch **seconds**, as the claim table stores them — not the
 * milliseconds `Date` takes. `formatPaidAge` handles the conversion; anything
 * else reading these has to do the same.
 */
export interface PartnerClaim {
  claimId: string;
  partnerId: string;
  /** Falls back to the partner id when the backend could not resolve branding. */
  partnerName: string;
  offerSlug: string;
  buyerEmail?: string;
  status: PartnerClaimStatus;
  stripeCheckoutSessionId: string;
  stripeSubscriptionId?: string;
  createdAt: number;
  paidAt?: number;
  reminderCount: number;
  /** Every send, oldest first — not just the most recent. */
  reminderSentAt: number[];
}

interface PartnerClaimsListResponse {
  claims: PartnerClaim[];
}

export interface ResendClaimEmailResult {
  sent: boolean;
  /** The claim's send total after this one, out of a lifetime ten. */
  reminderCount: number;
  /**
   * Every send including this one. Returned rather than inferred: the list shows
   * the count beside how long ago the last send was, and a client stamping its
   * own clock there would be writing a guess into a column read as a record of
   * what we actually sent.
   */
  reminderSentAt: number[];
}


/** How a claim search term was read. */
export type ClaimSearchBy =
  | "claimId"
  | "checkoutSession"
  | "subscription"
  | "customer"
  | "email"
  | "name"
  | "cardLast4";

/**
 * What Stripe knows about a payment that the claim record does not — the name on
 * the card and its last four digits, above all. This is the evidence support
 * judges a match by; the claim row stores none of it.
 */
export interface StripeEvidence {
  checkoutSessionId: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  email?: string;
  name?: string;
  cardLast4?: string;
  cardBrand?: string;
  amountTotal?: number;
  currency?: string;
  /** Epoch seconds. */
  created?: number;
}

/** A candidate, not a decision. */
export interface ClaimSearchHit {
  claim: PartnerClaim;
  evidence?: StripeEvidence;
  matchedOn: ClaimSearchBy;
}

export interface ClaimSearchResponse {
  hits: ClaimSearchHit[];
  resolvedSearchBy: ClaimSearchBy;
}

export interface AttachClaimResult {
  attached: boolean;
  claimId: string;
  partnerId: string;
  partnerName?: string;
  appUserId: string;
  email?: string;
  activeEntitlementIds?: string[];
}

export const PartnerClaimResource = {
  /**
   * The paid-but-unclaimed worklist, oldest payment first.
   *
   * `partnerId` is required for any status other than `paid`: only the paid rows
   * are indexed across all partners, and the backend refuses the rest rather
   * than answering from the wrong index.
   */
  listUnclaimed: (params?: {
    status?: PartnerClaimStatus;
    partnerId?: string;
  }) =>
    apiFetch<PartnerClaimsListResponse>("/admin/partner-claims/unclaimed", {
      searchParams: { status: params?.status, partnerId: params?.partnerId },
    }).then((r) => r.claims),

  /**
   * Mails a fresh 7-day link to the address on the claim, and nowhere else.
   *
   * Rejections carry a message meant to be shown as-is: the 429s name the
   * cooldown or the ten-send cap in the same words the member sees on the claim
   * page, so support and member are describing the same thing.
   */
  resendClaimEmail: (claimId: string) =>
    apiFetch<ResendClaimEmailResult>(
      `/admin/partner-claims/${claimId}/resend`,
      { method: "POST" }
    ),

  /**
   * Find a purchase from whatever the buyer could supply — an address, the name
   * on their card, its last four digits, or any Stripe id.
   *
   * Returns candidates. Deliberately never picks one, even on a single hit:
   * matching a payment to a person is a judgement made by whoever spoke to them,
   * and the stored address is not an identity.
   */
  search: (q: string, searchBy?: ClaimSearchBy | "auto") =>
    apiFetch<ClaimSearchResponse>("/admin/partner-claims/search", {
      searchParams: { q, searchBy },
    }),

  /**
   * Attach a paid-but-unclaimed purchase to an account, on the buyer's behalf.
   *
   * Irreversible from here: it grants a paid membership and moves the
   * subscription. `reason` is recorded on the audit trail and is rejected if it
   * says nothing.
   */
  attachToAccount: (
    userId: string,
    body: { claimId: string; reason: string; marketingOptIn?: boolean }
  ) =>
    apiFetch<AttachClaimResult>(`/admin/user/${userId}/partner-claim`, {
      method: "POST",
      body,
    }),
};
